import type { ReactNode } from 'react';
import { Check } from '../design-system';

export interface ProtectionCheckboxProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Label content beside the box — plain text or rich nodes. */
  children: ReactNode;
  /** id wiring so the label is announced with the control. */
  id?: string;
}

/**
 * Manual checkbox built from the MyPacksToolbar pattern (button + box span +
 * Check icon) — there is no DS `<Checkbox>`. Used by `ProtectionOptOutWarning`
 * for the opt-out acknowledgement. Colours flow through `--co-ProtectionAck-*`
 * with hex fallbacks so an un-themed build is self-contained.
 */
export function ProtectionCheckbox({
  checked,
  onChange,
  children,
  id,
}: ProtectionCheckboxProps) {
  return (
    <button
      type="button"
      id={id}
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="inline-flex items-start gap-2 text-left transition-opacity hover:opacity-90"
    >
      <span
        aria-hidden
        className="mt-px inline-flex size-[20px] shrink-0 items-center justify-center rounded-[7px] transition-colors"
        style={{
          background: checked
            ? 'var(--co-ProtectionAck-boxFillChecked, #23D11D)'
            : 'var(--co-ProtectionAck-boxFill, transparent)',
          border:
            '1px solid var(--co-ProtectionAck-boxBorder, rgba(222,222,224,0.29))',
          color: 'var(--co-ProtectionAck-checkColor, #0A0B0C)',
        }}
      >
        {checked && <Check size={14} strokeWidth={3} />}
      </span>
      <span
        className="text-[13px] font-medium leading-[18px]"
        style={{ color: 'var(--co-ProtectionAck-labelColor, rgba(255,255,255,0.85))' }}
      >
        {children}
      </span>
    </button>
  );
}
